import { CollectionsGrid } from "@/components/hymns/CollectionsGrid";
import { HymnPageLayout } from "@/components/hymns/HymnPageLayout";
import { HymnSearchBar } from "@/components/hymns/HymnSearchBar";
import { PopularHymnsSection } from "@/components/hymns/PopularHymnsSection";
import { TopHymnsSection } from "@/components/hymns/TopHymnsSection";
import {
  getPopularHymns,
  getSectionsSummary,
  getTopHymns,
  Hymn,
  SectionSummary,
} from "@/services/hymnsApi";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";

const PageChantsDesperanceCollectionGroupedGrid = () => {
  const [sections, setSections] = useState<SectionSummary[]>([]);
  const [popularHymns, setPopularHymns] = useState<Hymn[]>([]);
  const [topHymns, setTopHymns] = useState<Hymn[]>([]);
  const [loadingSections, setLoadingSections] = useState<boolean>(true);
  const [loadingPopular, setLoadingPopular] = useState<boolean>(true);
  const [loadingTop, setLoadingTop] = useState<boolean>(true);
  const [errorSections, setErrorSections] = useState<boolean>(false);
  const [errorPopular, setErrorPopular] = useState<boolean>(false);
  const [errorTop, setErrorTop] = useState<boolean>(false);

  // Fetch collections summary
  const loadSections = async () => {
    setLoadingSections(true);
    setErrorSections(false);
    try {
      const data = await getSectionsSummary();
      setSections(data);
    } catch (err) {
      console.error("Failed loading sections:", err);
      setErrorSections(true);
    } finally {
      setLoadingSections(false);
    }
  };

  // Fetch popular hymns
  const loadPopular = async () => {
    setLoadingPopular(true);
    setErrorPopular(false);
    try {
      const data = await getPopularHymns(6);
      setPopularHymns(data);
    } catch (err) {
      console.error("Failed loading popular hymns:", err);
      setErrorPopular(true);
    } finally {
      setLoadingPopular(false);
    }
  };

  // Fetch top hymns
  const loadTop = async () => {
    setLoadingTop(true);
    setErrorTop(false);
    try {
      const data = await getTopHymns();
      setTopHymns(data);
    } catch (err) {
      console.error("Failed loading top hymns:", err);
      setErrorTop(true);
    } finally {
      setLoadingTop(false);
    }
  };

  useEffect(() => {
    loadSections();
    loadPopular();
    loadTop();
  }, []);

  const canonicalUrl =
    typeof window !== "undefined" ? window.location.href : "";

  return (
    <HymnPageLayout>
      <Helmet>
        <title>
          Chants d'Espérance - Recueils d'Hymnes et Louanges | Église Garizim
        </title>
        <meta
          name="description"
          content="Consultez tous les recueils de Chants d'Espérance, Mélodies Joyeuses, Réveillons-Nous, Haïti Chante en Français, Kreyòl, English et Español."
        />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Chants d'Espérance - Recueils d'Hymnes et Louanges | Église Garizim"
        />
        <meta
          property="og:description"
          content="Consultez tous les recueils de Chants d'Espérance, Mélodies Joyeuses, Réveillons-Nous, Haïti Chante en Français, Kreyòl, English et Español."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Église Garizim" />
        <meta
          property="og:image"
          content="https://ik.imagekit.io/4apzi8lz0/egarizim/chant-esperance-api.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Chants d'Espérance - Recueils d'Hymnes et Louanges | Église Garizim"
        />
        <meta
          name="twitter:description"
          content="Consultez tous les recueils de Chants d'Espérance, Mélodies Joyeuses, Réveillons-Nous, Haïti Chante en Français, Kreyòl, English et Español."
        />
        <meta
          name="twitter:image"
          content="https://ik.imagekit.io/4apzi8lz0/egarizim/chant-esperance-api.jpg"
        />
      </Helmet>

      {/* Hero Header Section */}
      <section className="relative px-4 sm:px-6 lg:px-8 pt-4 sm:pt-8 pb-2 text-center max-w-5xl mx-auto">
        {/* Main Title */}
        <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-white tracking-tight leading-tight mb-2 sm:mb-4 drop-shadow-md">
          Chants d'Espérance
        </h1>

        <p className="text-slate-200 text-sm sm:text-xl max-w-2xl mx-auto leading-relaxed mb-4 sm:mb-8 drop-shadow">
          Chantez, méditez et louez le Seigneur
        </p>

        {/* Real-time Global Hymn Search */}
        <div className="mt-2 sm:mt-4 mb-2 max-w-3xl mx-auto">
          <HymnSearchBar />
        </div>

        {/* Quick stat highlights 
        <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 mt-8 text-slate-300 text-xs sm:text-sm">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-sky-400" />
            <span>Plus de 1 900 cantiques</span>
          </div>
          <div className="flex items-center gap-2">
            <Music className="w-4 h-4 text-amber-400" />
            <span>Kreyòl & Français</span>
          </div>
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-rose-400" />
            <span>Accès libre et gratuit</span>
          </div>
        </div>*/}
      </section>

      {/* Screenshot Ref: Browse the collections section */}
      <CollectionsGrid
        sections={sections}
        loading={loadingSections}
        hasError={errorSections}
        onRetry={loadSections}
      />

      {/* Popular this week: Hymns the community is singing */}
      <PopularHymnsSection
        hymns={popularHymns}
        loading={loadingPopular}
        hasError={errorPopular}
        onRetry={loadPopular}
      />

      {/* Top cantiques: Curated community-beloved hymns */}
      <TopHymnsSection
        hymns={topHymns}
        loading={loadingTop}
        hasError={errorTop}
        onRetry={loadTop}
      />
    </HymnPageLayout>
  );
};

export default PageChantsDesperanceCollectionGroupedGrid;
